// Typography-driven episode artwork — no stock photos.
// Alternates red (♀) and blue (♂) palettes to match the show's intersexual-dynamics duality.
import { Mic } from "lucide-react";

export default function EpisodeArtwork({ episode, size = "md" }) {
  const ep = episode || {};
  const num = ep.episode_number ?? 0;
  // Red and blue alternating palettes, drawn from the cover artwork's ♀/♂ duality.
  const palettes = [
    { bg: "#0A0A0A", accent: "#E10600", text: "#FFFFFF", glyph: "♀" }, // black + red
    { bg: "#0A0A0A", accent: "#1E6FE0", text: "#FFFFFF", glyph: "♂" }, // black + blue
    { bg: "#E10600", accent: "#FFFFFF", text: "#FFFFFF", glyph: "♀" }, // red + white
    { bg: "#1E6FE0", accent: "#FFFFFF", text: "#FFFFFF", glyph: "♂" }, // blue + white
    { bg: "#FFFFFF", accent: "#E10600", text: "#0A0A0A", glyph: "♀" }, // white + red
    { bg: "#FFFFFF", accent: "#1E6FE0", text: "#0A0A0A", glyph: "♂" }, // white + blue
  ];
  const p = palettes[(num - 1) % palettes.length] || palettes[0];

  return (
    <div
      className="relative w-full aspect-square overflow-hidden"
      style={{ background: p.bg, color: p.text }}
    >
      {/* halftone dot pattern */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(circle, ${p.accent} 1.2px, transparent 1.5px)`,
          backgroundSize: size === "lg" ? "14px 14px" : "10px 10px",
        }}
      />

      {/* corner accent stripes */}
      <div
        className="absolute top-0 left-0 h-1 w-12"
        style={{ background: p.accent }}
      />
      <div
        className="absolute top-0 left-0 w-1 h-12"
        style={{ background: p.accent }}
      />
      <div
        className="absolute bottom-0 right-0 h-1 w-12"
        style={{ background: p.accent }}
      />
      <div
        className="absolute bottom-0 right-0 w-1 h-12"
        style={{ background: p.accent }}
      />

      {/* gender symbol watermark — subtle, top-left */}
      <span
        aria-hidden
        className="absolute -top-4 -left-2 leading-none font-black select-none pointer-events-none"
        style={{
          color: p.accent,
          opacity: 0.18,
          fontSize: size === "lg" ? "16rem" : size === "sm" ? "5rem" : "10rem",
        }}
      >
        {p.glyph}
      </span>

      {/* show mark */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        <span
          className="w-6 h-6 flex items-center justify-center"
          style={{ background: p.accent }}
        >
          <Mic className="w-3.5 h-3.5" style={{ color: p.bg }} />
        </span>
        <span
          className="font-display text-xs tracking-tight uppercase leading-none"
          style={{ color: p.text }}
        >
          PYP
        </span>
      </div>

      {/* episode number block */}
      <div className="absolute inset-0 flex flex-col justify-center items-center px-4 text-center">
        <p
          className="text-[10px] tracking-[0.3em] uppercase font-bold"
          style={{ color: p.accent }}
        >
          {ep.number || `EP ${String(num).padStart(2, "0")}`}
        </p>
        <p
          className={`font-display leading-[0.85] mt-2 ${
            size === "lg" ? "text-[8rem]" : size === "sm" ? "text-5xl" : "text-7xl"
          }`}
          style={{ color: p.text }}
        >
          {String(num).padStart(2, "0")}
        </p>
        {ep.title && size !== "sm" && (
          <p
            className={`font-display uppercase mt-3 leading-none ${
              size === "lg" ? "text-2xl" : "text-base"
            }`}
            style={{ color: p.text }}
          >
            {ep.title}
          </p>
        )}
      </div>

      {/* bottom waveform decoration */}
      <div className="absolute bottom-3 left-3 right-12 flex items-end gap-[2px] h-4 opacity-70">
        {Array.from({ length: 28 }).map((_, i) => (
          <span
            key={i}
            className="flex-1"
            style={{
              background: p.accent,
              height: `${20 + Math.abs(Math.sin((i + num) * 0.7)) * 80}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
